
/** A `[time, value]` curve, exactly as the manifest carries it. */
export type Curve = readonly (readonly number[])[];

export function sampleCurve(curve: Curve, t: number): number {
  const n = curve.length;
  if (n === 0) return 0;
  if (n === 1) return curve[0][1];
  if (t <= curve[0][0]) return curve[0][1];
  const last = curve[n - 1];
  if (t >= last[0]) return last[1];
  // Linear scan rather than a binary search: these tables are two or three
  // rows, and `equal_range` on three elements is slower than looking at them.
  for (let i = 1; i < n; i++) {
    const hi = curve[i];
    if (t > hi[0]) continue;
    const lo = curve[i - 1];
    const span = hi[0] - lo[0];
    if (span <= 0) return hi[1];
    const alpha = (t - lo[0]) / span;
    return lo[1] * (1 - alpha) + hi[1] * alpha;
  }
  return last[1];
}

export function emissionThisStep(
  ratePerS: number,
  dtS: number,
  residue: number,
  live: number,
  maxPerEmitter: number,
  room: number,
): { count: number; residue: number } {
  const wanted = ratePerS * dtS + residue;
  let count = Math.floor(wanted);
  const carry = wanted - count;
  count -= Math.max(0, live + count - maxPerEmitter);
  if (count > room) count = room;
  if (count < 0) count = 0;
  return { count, residue: carry };
}

export function lifeFraction(lifeS: number, leftS: number): number {
  if (lifeS <= 0) return 1;
  const phase = (lifeS - leftS) / lifeS;
  return phase <= 0 ? 0 : phase >= 1 ? 1 : phase;
}

export const DRAG_REFERENCE_HZ = 60;

export function dragFactor(resistance: number, dtS: number): number {
  if (resistance <= 0) return 1;
  if (resistance >= 1) return 0;
  return Math.pow(1 - resistance, dtS * DRAG_REFERENCE_HZ);
}

// --------------------------------------------------------------- the manifest

/** One `Group Particle` from a `.mse`, as `build_effects.py` writes it. */
export interface EmitterDef {
  readonly startS: number;
  readonly maxParticles: number;
  readonly cycleS: number;
  readonly cycleLoop: boolean;
  readonly loopCount: number;
  readonly shape: number;
  readonly advanced: number;
  readonly emitFromEdge: boolean;
  readonly radiusMm: number;
  readonly boxMm: readonly number[];
  readonly jitterMm: readonly number[];
  readonly positionMm: Curve;
  readonly emittingSizeMm: Curve;
  readonly angularVelocityDegS: Curve;
  readonly dirXMm: Curve;
  readonly dirYMm: Curve;
  readonly dirZMm: Curve;
  readonly velocity: Curve;
  readonly ratePerS: Curve;
  readonly lifeS: Curve;
  readonly halfWidthMm: Curve;
  readonly halfHeightMm: Curve;
  readonly srcBlend: number;
  readonly dstBlend: number;
  readonly billboard: number;
  readonly rotationType: number;
  readonly rotationSpeedDegS: number;
  readonly rotationStartDeg: readonly number[];
  readonly attach: boolean;
  readonly stretch: boolean;
  readonly texAniType: number;
  readonly texAniDelayS: number;
  readonly texAniRandomStart: boolean;
  readonly gravityMmS2: Curve;
  readonly airResistance: Curve;
  readonly scaleX: Curve;
  readonly scaleY: Curve;
  readonly red: Curve;
  readonly green: Curve;
  readonly blue: Curve;
  readonly alpha: Curve;
  readonly rotationDeg: Curve;
  readonly frames: readonly string[];
}

export interface EffectDef {
  readonly source: string;
  readonly boundingRadiusMm: number;
  /** `BoundingSpherePosition` (`EffectData.cpp:42-44`), in millimetres, in the
   *  file's own frame. */
  readonly boundingPositionMm?: readonly number[];
  readonly durationS: number;
  readonly emitters: readonly EmitterDef[];
}

export interface EffectManifest {
  readonly mmPerOriginalUnit: number;
  readonly atlas: { readonly image: string; readonly width: number; readonly height: number };
  /** Texture key -> `[x, y, w, h]` in atlas pixels. */
  readonly textures: Readonly<Record<string, readonly number[]>>;
  /** Skill vnum -> effect key. */
  readonly skillEffects: Readonly<Record<string, string>>;
  readonly weaponRefineFamilies?: Readonly<Record<string, string>>;
  readonly effects: Readonly<Record<string, EffectDef>>;
}

// ------------------------------------------------------------------- geometry

/** How a quad is oriented. Four modes, collapsed from the original's six
 *  billboard types plus its stretch flag -- see `billboardModeOf`. */
export const MODE_SPHERICAL = 0;
export const MODE_UPRIGHT = 1;
export const MODE_LIE = 2;
export const MODE_STRETCH = 3;

export function billboardModeOf(def: EmitterDef): number {
  if (def.stretch) return MODE_STRETCH;
  if (def.billboard === 2) return MODE_UPRIGHT;
  if (def.billboard === 3) return MODE_LIE;
  return MODE_SPHERICAL;
}

/** Additive (`D3DBLEND_ONE`) or straight alpha. The only two destination
 *  factors any of the nine effects use, checked at build time. */
export function isAdditive(def: EmitterDef): boolean {
  return def.dstBlend === 2;
}

// --------------------------------------------------------------------- budget

/** The particle pool. Sized against the worst case this build can reach: a
 *  hundred monsters is a hundred hits a few seconds apart, and each `hit`
 *  effect is four emitters whose own caps sum to 19 particles. */
export const MAX_PARTICLES = 3072;

/** Live emitter instances. One effect contributes between two and six. */
export const MAX_EMITTERS = 256;

/** The longest step ever integrated, in seconds. A backgrounded tab resumes
 *  with a `dt` of whole seconds, and the honest answer to that is to lose the
 *  intervening motion rather than to spend a frame catching up on it. */
export const MAX_STEP_S = 0.1;

export const MAX_SUBSTEP_S = 1 / 60;

// ------------------------------------------------------------- the simulation

/** A deterministic-in-tests source of randomness. `Math.random` in production;
 *  a seeded sequence in a test that needs to assert where a particle went. */
export type Random = () => number;

/** Where and how one effect was fired. */
export interface SpawnOptions {
  readonly xMm: number;
  readonly yMm: number;
  readonly zMm: number;
  /** Radians, counter-clockwise from east. Rotates the effect's own local
   *  frame, so a blade arc points the way the caster is facing. */
  readonly headingRad?: number;
  /** Scales every length in the effect. 1 is the original's own size. */
  readonly scale?: number;
  readonly followId?: number;
  /** A placed effect's local-to-world axes, column-major 3x3, which a map
   *  placement's yaw, pitch and roll build (`placedeffects.ts`'s
   *  `effectBasis`). Replaces the heading when given. */
  readonly basis?: ArrayLike<number>;
  /** The caller's handle for this one spawn, which `deactivate` names; 0, the
   *  default, is no handle. */
  readonly instance?: number;
}

/** No entity: an emitter that stands where it was fired. Not 0, which is a
 *  real entity id on this wire. */
const NO_FOLLOW = -1;

interface LiveEmitter {
  def: EmitterDef;
  /** Seconds since the effect was spawned, including the emitter's own delay. */
  ageS: number;
  /** Seconds within the current cycle. `m_fLocalTime`. */
  localS: number;
  loopsLeft: number;
  residue: number;
  emitting: boolean;
  live: number;
  originX: number;
  originY: number;
  originZ: number;
  /** The entity this emitter is attached to, or `NO_FOLLOW`. */
  followId: number;
  /** The emitter's origin *relative* to that entity's own origin -- the fixed
   *  `matTranslation` half of `UpdateAttachingInstances`'s product. Unused
   *  when `followId` is `NO_FOLLOW`. */
  offsetX: number;
  offsetY: number;
  offsetZ: number;
  cos: number;
  sin: number;
  /** `SpawnOptions.basis`, or null for an effect turned by its heading. */
  basis: ArrayLike<number> | null;
  /** The axes a followed anchor last turned this emitter to, owned here so the
   *  next turn can read the one before it. */
  turned: Float64Array | null;
  scale: number;
  /** Monotonic, so the oldest effect can be identified for eviction. */
  serial: number;
  /** `SpawnOptions.instance`. */
  instance: number;
  /** Index into `frameCells`, filled at construction from the atlas. */
  cells: Float32Array;
  mode: number;
  additive: boolean;
}

/** Floats per particle in the interleaved buffer `fill` writes. */
export const INSTANCE_FLOATS = 18;

export class ParticleField {
  private readonly manifest: EffectManifest;
  private readonly random: Random;
  private readonly capacity: number;

  private readonly emitters: LiveEmitter[] = [];
  private serial = 0;

  private readonly followed = new Map<number, {
    xMm: number; yMm: number; zMm: number; basis: ArrayLike<number> | null;
  }>();
  private readonly previousAxes = new Float64Array(9);

  // The particle pool, structure-of-arrays. `count` live at the front.
  private count = 0;
  private readonly px: Float32Array;
  private readonly py: Float32Array;
  private readonly pz: Float32Array;
  private readonly vx: Float32Array;
  private readonly vy: Float32Array;
  private readonly vz: Float32Array;
  private readonly lastX: Float32Array;
  private readonly lastY: Float32Array;
  private readonly lastZ: Float32Array;
  private readonly startX: Float32Array;
  private readonly startY: Float32Array;
  private readonly startZ: Float32Array;
  private readonly lifeS: Float32Array;
  private readonly leftS: Float32Array;
  private readonly halfW: Float32Array;
  private readonly halfH: Float32Array;
  private readonly rotDeg: Float32Array;
  private readonly rotSpeed: Float32Array;
  private readonly frameTimer: Float32Array;
  private readonly frame: Int32Array;
  private readonly owner: Int32Array;
  // Written by `step`, read by `fill`.
  private readonly outScaleX: Float32Array;
  private readonly outScaleY: Float32Array;
  private readonly outR: Float32Array;
  private readonly outG: Float32Array;
  private readonly outB: Float32Array;
  private readonly outA: Float32Array;

  constructor(
    manifest: EffectManifest,
    random: Random = Math.random,
    capacity = MAX_PARTICLES,
    private readonly emitterCapacity = MAX_EMITTERS,
  ) {
    this.manifest = manifest;
    this.random = random;
    this.capacity = capacity;
    const n = capacity;
    this.px = new Float32Array(n);
    this.py = new Float32Array(n);
    this.pz = new Float32Array(n);
    this.vx = new Float32Array(n);
    this.vy = new Float32Array(n);
    this.vz = new Float32Array(n);
    this.lastX = new Float32Array(n);
    this.lastY = new Float32Array(n);
    this.lastZ = new Float32Array(n);
    this.startX = new Float32Array(n);
    this.startY = new Float32Array(n);
    this.startZ = new Float32Array(n);
    this.lifeS = new Float32Array(n);
    this.leftS = new Float32Array(n);
    this.halfW = new Float32Array(n);
    this.halfH = new Float32Array(n);
    this.rotDeg = new Float32Array(n);
    this.rotSpeed = new Float32Array(n);
    this.frameTimer = new Float32Array(n);
    this.frame = new Int32Array(n);
    this.owner = new Int32Array(n);
    this.outScaleX = new Float32Array(n);
    this.outScaleY = new Float32Array(n);
    this.outR = new Float32Array(n);
    this.outG = new Float32Array(n);
    this.outB = new Float32Array(n);
    this.outA = new Float32Array(n);
  }

  get particleCount(): number {
    return this.count;
  }

  get emitterCount(): number {
    return this.emitters.length;
  }

  /** Whether the manifest knows this effect at all. Callers ask before
   *  spawning so a missing effect is a silent no-op rather than a throw in the
   *  middle of a frame. */
  has(key: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.manifest.effects, key);
  }

  spawn(key: string, options: SpawnOptions): number {
    const effect = this.manifest.effects[key];
    if (effect === undefined) return 0;

    const wanted = effect.emitters.length;
    while (this.emitters.length + wanted > this.emitterCapacity && this.emitters.length > 0) {
      this.dropOldest();
    }

    const heading = options.headingRad ?? 0;
    const scale = options.scale ?? 1;
    const serial = this.serial++;
    const followId = options.followId ?? NO_FOLLOW;
    // The fixed local translation the moving body is multiplied by. Zero for
    // an entity never followed, so the effect snaps onto the body the first
    // time it is followed rather than being drawn a world away from it.
    const anchor = followId === NO_FOLLOW ? undefined : this.followed.get(followId);
    const offsetX = anchor === undefined ? 0 : options.xMm - anchor.xMm;
    const offsetY = anchor === undefined ? 0 : options.yMm - anchor.yMm;
    const offsetZ = anchor === undefined ? 0 : options.zMm - anchor.zMm;
    let added = 0;
    for (const def of effect.emitters) {
      if (this.emitters.length >= this.emitterCapacity) break;
      this.emitters.push({
        def,
        ageS: 0,
        localS: 0,
        // `m_iLoopCount` starts at the file's own LoopCount; zero means forever
        // (`ParticleSystemInstance.cpp:284`: `--m_iLoopCount != 0` is true for 0).
        loopsLeft: def.loopCount,
        residue: 0,
        emitting: true,
        live: 0,
        originX: options.xMm,
        originY: options.yMm,
        originZ: options.zMm,
        followId,
        offsetX,
        offsetY,
        offsetZ,
        cos: Math.cos(heading),
        sin: Math.sin(heading),
        basis: options.basis ?? null,
        turned: null,
        scale,
        serial,
        instance: options.instance ?? 0,
        cells: this.cellsFor(def),
        mode: billboardModeOf(def),
        additive: isAdditive(def),
      });
      added += 1;
    }
    return added;
  }

  /** `CEffectManager::DeactiveEffectInstance` (`EffectManager.cpp:244-253`):
   *  every emitter of that spawn stops making particles
   *  (`ParticleSystemInstance.cpp:340`) and those it made live out their life. */
  deactivate(instance: number): void {
    if (instance === 0) return;
    for (const emitter of this.emitters) {
      if (emitter.instance === instance) emitter.emitting = false;
    }
  }

  /** Stop everything. A reconnect is a new world and last world's sparks do not
   *  belong in it. */
  clear(): void {
    this.emitters.length = 0;
    this.count = 0;
    this.followed.clear();
  }

  /** Move a body its effects hang on, and, given `basis` (column-major 3x3),
   *  turn them with it: the rotation half of the global matrix
   *  `UpdateAttachingInstances` sets (`ActorInstanceAttach.cpp:553-563`). */
  follow(entityId: number, xMm: number, yMm: number, zMm: number, basis: ArrayLike<number> | null = null): void {
    const row = this.followed.get(entityId);
    if (row === undefined) this.followed.set(entityId, { xMm, yMm, zMm, basis });
    else {
      row.xMm = xMm;
      row.yMm = yMm;
      row.zMm = zMm;
      row.basis = basis;
    }
  }

  /** Whether this field is tracking a body for `entityId`. */
  following(entityId: number): boolean {
    return this.followed.has(entityId);
  }

  unfollow(entityId: number): void {
    this.followed.delete(entityId);
    for (let i = this.emitters.length - 1; i >= 0; i--) {
      if (this.emitters[i].followId === entityId) this.retire(i);
    }
  }

  stopFollowing(entityId: number, key: string): void {
    const effect = this.manifest.effects[key];
    if (effect === undefined) return;
    const defs = new Set<EmitterDef>(effect.emitters);
    for (let i = this.emitters.length - 1; i >= 0; i--) {
      const emitter = this.emitters[i];
      if (emitter.followId === entityId && defs.has(emitter.def)) this.retire(i);
    }
  }

  /** Whether any emitter of `key` is currently attached to `entityId` -- the
   *  `m_adwCRCAffectEffect[eAffect]` test `__SetAffect` makes before it
   *  attaches a second copy (`InstanceBaseEffect.cpp:947-951`). */
  isFollowing(entityId: number, key: string): boolean {
    const effect = this.manifest.effects[key];
    if (effect === undefined) return false;
    const defs = new Set<EmitterDef>(effect.emitters);
    for (const emitter of this.emitters) {
      if (emitter.followId === entityId && defs.has(emitter.def)) return true;
    }
    return false;
  }

  step(dtS: number): void {
    let remaining = Math.min(Math.max(dtS, 0), MAX_STEP_S);
    while (remaining > 0) {
      const slice = Math.min(remaining, MAX_SUBSTEP_S);
      this.stepOnce(slice);
      remaining -= slice;
    }
  }

  /** One fixed sub-step. See `MAX_SUBSTEP_S` for why `step` never calls this
   *  with a whole frame's worth of time. */
  private stepOnce(dt: number): void {
    if (dt <= 0) return;

    this.reanchor();

    for (const emitter of this.emitters) {
      emitter.ageS += dt;
      if (emitter.ageS < emitter.def.startS) continue;
      emitter.localS += dt;
      if (emitter.localS >= emitter.def.cycleS) {
        if (emitter.def.cycleLoop && --emitter.loopsLeft !== 0) {
          if (emitter.loopsLeft < 0) emitter.loopsLeft = 0;
          emitter.localS -= emitter.def.cycleS;
        } else {
          emitter.emitting = false;
          emitter.loopsLeft = 1;
        }
      }
    }

    this.integrate(dt);

    for (let i = 0; i < this.emitters.length; i++) {
      const emitter = this.emitters[i];
      if (!emitter.emitting) continue;
      if (emitter.ageS < emitter.def.startS) continue;
      // The index is passed rather than looked up: a particle stores its
      // emitter's position in this array, and searching for it once per
      // emitter per step would make emission quadratic in how many effects
      // are on screen -- which is exactly the case the budget exists for.
      this.emit(emitter, i, dt);
    }

    // An emitter that has stopped emitting and has nothing left alive is done.
    // Removed through `retire` rather than by splicing here, because every
    // particle carries its emitter's *index* and a bare splice would silently
    // repoint every particle after it at the wrong emitter.
    for (let i = this.emitters.length - 1; i >= 0; i--) {
      const emitter = this.emitters[i];
      if (!emitter.emitting && emitter.live === 0) this.retire(i);
    }
  }

  /** Pack the live particles for drawing, additive from the front and alpha
   *  from the back. `shown`, when given, is asked of each emitter's follow id
   *  and a particle whose emitter it refuses is not packed: a placed effect
   *  the frustum hides keeps simulating and is not drawn
   *  (`GrpObjectInstance.cpp:33-52`). */
  fill(out: Float32Array, shown?: (followId: number) => boolean): { count: number; additive: number } {
    const stride = INSTANCE_FLOATS;
    const room = Math.floor(out.length / stride);
    let head = 0;
    let packable = 0;
    for (let i = 0; i < this.count; i++) {
      const emitter = this.emitters[this.owner[i]];
      if (emitter !== undefined && (shown === undefined || shown(emitter.followId))) packable += 1;
    }
    let tail = Math.min(packable, room);
    let written = 0;

    for (let i = 0; i < this.count && written < room; i++) {
      const emitter = this.emitters[this.owner[i]];
      if (emitter === undefined) continue;
      if (shown !== undefined && !shown(emitter.followId)) continue;
      const at = (emitter.additive ? head++ : --tail) * stride;
      const cell = emitter.cells;
      const frame = this.frame[i] * 4;
      out[at] = this.px[i];
      out[at + 1] = this.py[i];
      out[at + 2] = this.pz[i];
      out[at + 3] = this.halfW[i] * this.outScaleX[i];
      out[at + 4] = this.halfH[i] * this.outScaleY[i];
      out[at + 5] = (this.rotDeg[i] * Math.PI) / 180;
      out[at + 6] = this.outR[i];
      out[at + 7] = this.outG[i];
      out[at + 8] = this.outB[i];
      out[at + 9] = this.outA[i];
      out[at + 10] = cell[frame];
      out[at + 11] = cell[frame + 1];
      out[at + 12] = cell[frame + 2];
      out[at + 13] = cell[frame + 3];
      out[at + 14] = emitter.mode;
      out[at + 15] = this.px[i] - this.lastX[i];
      out[at + 16] = this.py[i] - this.lastY[i];
      out[at + 17] = this.pz[i] - this.lastZ[i];
      written += 1;
    }
    return { count: written, additive: head };
  }

  // ------------------------------------------------------------------ private

  /** The atlas rectangles this emitter's frames live in, flattened, in 0..1
   *  texture coordinates. Resolved once at spawn rather than per particle. */
  private cellsFor(def: EmitterDef): Float32Array {
    const atlas = this.manifest.atlas;
    const cells = new Float32Array(Math.max(1, def.frames.length) * 4);
    for (let i = 0; i < def.frames.length; i++) {
      const rect = this.manifest.textures[def.frames[i]];
      if (rect === undefined) continue;
      cells[i * 4] = rect[0] / atlas.width;
      cells[i * 4 + 1] = rect[1] / atlas.height;
      cells[i * 4 + 2] = rect[2] / atlas.width;
      cells[i * 4 + 3] = rect[3] / atlas.height;
    }
    return cells;
  }

  private reanchor(): void {
    if (this.followed.size === 0) return;
    const was = this.previousAxes;
    for (let i = 0; i < this.emitters.length; i++) {
      const emitter = this.emitters[i];
      if (emitter.followId === NO_FOLLOW) continue;
      const body = this.followed.get(emitter.followId);
      if (body === undefined) continue;
      const x = body.xMm + emitter.offsetX;
      const y = body.yMm + emitter.offsetY;
      const z = body.zMm + emitter.offsetZ;
      const fromX = emitter.originX;
      const fromY = emitter.originY;
      const fromZ = emitter.originZ;
      emitter.originX = x;
      emitter.originY = y;
      emitter.originZ = z;
      const axes = body.basis;
      let turning = false;
      if (axes !== null) {
        // The axes this emitter stood on until now: a turn it was given, or
        // the heading it was spawned with.
        if (emitter.basis === null) was.set([emitter.cos, emitter.sin, 0, -emitter.sin, emitter.cos, 0, 0, 0, 1]);
        else for (let k = 0; k < 9; k++) was[k] = emitter.basis[k];
        for (let k = 0; k < 9; k++) if (was[k] !== axes[k]) turning = true;
        const turned = emitter.turned ?? (emitter.turned = new Float64Array(9));
        for (let k = 0; k < 9; k++) turned[k] = axes[k];
        emitter.basis = turned;
      }
      // `m_bAttachFlag`: the cloud lives in the effect's local space and every
      // draw puts it through the current global matrix (`ParticleInstance.cpp:261-275`);
      // anything else was placed in the world when it was born.
      if (!emitter.def.attach) continue;
      if (!turning && x === fromX && y === fromY && z === fromZ) continue;
      for (let p = 0; p < this.count; p++) {
        if (this.owner[p] !== i) continue;
        if (!turning) {
          this.px[p] += x - fromX; this.py[p] += y - fromY; this.pz[p] += z - fromZ;
          this.lastX[p] += x - fromX; this.lastY[p] += y - fromY; this.lastZ[p] += z - fromZ;
          this.startX[p] += x - fromX; this.startY[p] += y - fromY; this.startZ[p] += z - fromZ;
          continue;
        }
        const b = emitter.basis!;
        const move = (px: number, py: number, pz: number, origin: boolean): [number, number, number] => {
          const rx = origin ? px - fromX : px;
          const ry = origin ? py - fromY : py;
          const rz = origin ? pz - fromZ : pz;
          // Back into local space through the old axes' transpose, out through the new.
          const lx = was[0] * rx + was[1] * ry + was[2] * rz;
          const ly = was[3] * rx + was[4] * ry + was[5] * rz;
          const lz = was[6] * rx + was[7] * ry + was[8] * rz;
          return [
            (origin ? x : 0) + b[0] * lx + b[3] * ly + b[6] * lz,
            (origin ? y : 0) + b[1] * lx + b[4] * ly + b[7] * lz,
            (origin ? z : 0) + b[2] * lx + b[5] * ly + b[8] * lz,
          ];
        };
        [this.px[p], this.py[p], this.pz[p]] = move(this.px[p], this.py[p], this.pz[p], true);
        [this.lastX[p], this.lastY[p], this.lastZ[p]] = move(this.lastX[p], this.lastY[p], this.lastZ[p], true);
        [this.startX[p], this.startY[p], this.startZ[p]] = move(this.startX[p], this.startY[p], this.startZ[p], true);
        [this.vx[p], this.vy[p], this.vz[p]] = move(this.vx[p], this.vy[p], this.vz[p], false);
      }
    }
  }

  private dropOldest(): void {
    let oldest = this.emitters[0].serial;
    for (const emitter of this.emitters) {
      if (emitter.serial < oldest) oldest = emitter.serial;
    }
    for (let i = this.emitters.length - 1; i >= 0; i--) {
      if (this.emitters[i].serial === oldest) this.retire(i);
    }
  }

  /** Remove emitter `index` and every particle that belongs to it. */
  private retire(index: number): void {
    for (let i = this.count - 1; i >= 0; i--) {
      if (this.owner[i] === index) this.kill(i);
    }
    this.emitters.splice(index, 1);
    // Owners are indices into the array that just shifted.
    for (let i = 0; i < this.count; i++) {
      if (this.owner[i] > index) this.owner[i] -= 1;
    }
  }

  /** Swap-remove. Order in the pool is not meaningful, so the last live
   *  particle fills the hole and nothing is copied twice. */
  private kill(i: number): void {
    const owner = this.owner[i];
    const emitter = this.emitters[owner];
    if (emitter !== undefined) emitter.live -= 1;
    const last = this.count - 1;
    if (i !== last) {
      this.px[i] = this.px[last]; this.py[i] = this.py[last]; this.pz[i] = this.pz[last];
      this.vx[i] = this.vx[last]; this.vy[i] = this.vy[last]; this.vz[i] = this.vz[last];
      this.lastX[i] = this.lastX[last]; this.lastY[i] = this.lastY[last]; this.lastZ[i] = this.lastZ[last];
      this.startX[i] = this.startX[last]; this.startY[i] = this.startY[last]; this.startZ[i] = this.startZ[last];
      this.lifeS[i] = this.lifeS[last]; this.leftS[i] = this.leftS[last];
      this.halfW[i] = this.halfW[last]; this.halfH[i] = this.halfH[last];
      this.rotDeg[i] = this.rotDeg[last]; this.rotSpeed[i] = this.rotSpeed[last];
      this.frameTimer[i] = this.frameTimer[last]; this.frame[i] = this.frame[last];
      this.owner[i] = this.owner[last];
      this.outScaleX[i] = this.outScaleX[last]; this.outScaleY[i] = this.outScaleY[last];
      this.outR[i] = this.outR[last]; this.outG[i] = this.outG[last];
      this.outB[i] = this.outB[last]; this.outA[i] = this.outA[last];
    }
    this.count = last;
  }

  private integrate(dt: number): void {
    for (let i = this.count - 1; i >= 0; i--) {
      const left = this.leftS[i] - dt;
      if (left < 0) {
        this.kill(i);
        continue;
      }
      this.leftS[i] = left;
      const emitter = this.emitters[this.owner[i]];
      if (emitter === undefined) continue;
      const def = emitter.def;
      const phase = lifeFraction(this.lifeS[i], left);

      const drag = dragFactor(sampleCurve(def.airResistance, phase), dt);
      if (drag !== 1) {
        this.vx[i] *= drag; this.vy[i] *= drag; this.vz[i] *= drag;
      }
      const gravity = sampleCurve(def.gravityMmS2, phase);
      if (gravity !== 0) this.vz[i] -= gravity * emitter.scale * dt;

      this.lastX[i] = this.px[i];
      this.lastY[i] = this.py[i];
      this.lastZ[i] = this.pz[i];
      this.px[i] += this.vx[i] * dt;
      this.py[i] += this.vy[i] * dt;
      this.pz[i] += this.vz[i] * dt;

      // Angular velocity swings the whole particle about the effect's own
      // vertical axis through the point it was emitted from
      // (`ParticleInstance.cpp:83-118`, the `fAngle` branch).
      const spin = sampleCurve(def.angularVelocityDegS, emitter.localS);
      if (spin !== 0) {
        const angle = (spin * dt * Math.PI) / 180;
        const c = Math.cos(angle);
        const s = Math.sin(angle);
        const rx = this.px[i] - this.startX[i];
        const ry = this.py[i] - this.startY[i];
        this.px[i] = c * rx + s * ry + this.startX[i];
        this.py[i] = -s * rx + c * ry + this.startY[i];
      }

      this.rotDeg[i] += this.rotSpeed[i] * dt;

      if (def.frames.length > 1 && def.texAniDelayS > 1e-6) {
        this.frameTimer[i] += dt;
        while (this.frameTimer[i] >= def.texAniDelayS) {
          this.frameTimer[i] -= def.texAniDelayS;
          // Counter-clockwise counts down, everything else counts up; both wrap.
          // `CTextureAnimation{CW,CCW}Decorator`, EffectUpdateDecorator.h:178-224.
          const next = def.texAniType === 2 ? this.frame[i] - 1 : this.frame[i] + 1;
          this.frame[i] = (next + def.frames.length) % def.frames.length;
        }
      }

      this.outScaleX[i] = sampleCurve(def.scaleX, phase);
      this.outScaleY[i] = sampleCurve(def.scaleY, phase);
      this.outR[i] = sampleCurve(def.red, phase);
      this.outG[i] = sampleCurve(def.green, phase);
      this.outB[i] = sampleCurve(def.blue, phase);
      this.outA[i] = sampleCurve(def.alpha, phase);
    }
  }

  /** `CParticleSystemInstance::CreateParticles`, for one emitter, one step. */
  private emit(emitter: LiveEmitter, ownerIndex: number, dt: number): void {
    const def = emitter.def;
    const t = emitter.localS;
    const room = this.capacity - this.count;
    if (room <= 0) return;

    const { count, residue } = emissionThisStep(
      sampleCurve(def.ratePerS, t),
      dt,
      emitter.residue,
      emitter.live,
      def.maxParticles,
      room,
    );
    emitter.residue = residue;
    if (count <= 0) return;

    const life = sampleCurve(def.lifeS, t);
    // The original bails out of the *whole* emission when the life curve has
    // reached zero (`ParticleSystemInstance.cpp:62`), which is how an emitter
    // whose life table ends at 0 stops producing without stopping its cycle.
    if (life <= 0) return;

    const emittingSize = sampleCurve(def.emittingSizeMm, t) * emitter.scale;
    const halfW = sampleCurve(def.halfWidthMm, t) * emitter.scale;
    const halfH = sampleCurve(def.halfHeightMm, t) * emitter.scale;
    const speed = sampleCurve(def.velocity, t);
    const dirX = sampleCurve(def.dirXMm, t);
    const dirY = sampleCurve(def.dirYMm, t);
    const dirZ = sampleCurve(def.dirZMm, t);

    // The emitter's own offset from the effect's origin, in local space.
    let baseX = 0;
    let baseY = 0;
    let baseZ = 0;
    if (def.positionMm.length > 0) {
      const row = def.positionMm;
      baseX = sampleAxis(row, t, 1);
      baseY = sampleAxis(row, t, 2);
      baseZ = sampleAxis(row, t, 3);
    }

    for (let n = 0; n < count; n++) {
      const i = this.count;
      const local = this.shapeOffset(def, emittingSize);
      const lx = local[0] * emitter.scale + baseX * emitter.scale;
      const ly = local[1] * emitter.scale + baseY * emitter.scale;
      const lz = local[2] * emitter.scale + baseZ * emitter.scale;

      let vlx = 0;
      let vly = 0;
      let vlz = 0;
      // `EMITTER_ADVANCED_TYPE_OUTER`/`_INNER`: fly away from, or toward, the
      // point the emitter sits at, at a flat 100 units a second before the
      // velocity multiplier (`ParticleSystemInstance.cpp:173-192`).
      if (def.advanced === 1 || def.advanced === 2) {
        const sign = def.advanced === 2 ? -1 : 1;
        const dx = lx - baseX * emitter.scale;
        const dy = ly - baseY * emitter.scale;
        const dz = lz - baseZ * emitter.scale;
        const length = Math.hypot(dx, dy, dz);
        if (def.shape === 0 && def.advanced === 1) {
          vlx = (this.random() * 2 - 1) * 1000;
          vly = (this.random() * 2 - 1) * 1000;
          vlz = (this.random() * 2 - 1) * 1000;
        } else if (length > 0) {
          vlx = (dx / length) * 1000 * sign;
          vly = (dy / length) * 1000 * sign;
          vlz = (dz / length) * 1000 * sign;
        }
      }
      vlx += dirX;
      vly += dirY;
      vlz += dirZ;
      // The jitter half-widths. `EmittingDirection` is not a direction; see
      // `build_effects.py`'s `convert_emitter`.
      if (def.jitterMm[0] > 0) vlx += (this.random() - 0.5) * def.jitterMm[0];
      if (def.jitterMm[1] > 0) vly += (this.random() - 0.5) * def.jitterMm[1];
      if (def.jitterMm[2] > 0) vlz += (this.random() - 0.5) * def.jitterMm[2];
      vlx *= speed * emitter.scale;
      vly *= speed * emitter.scale;
      vlz *= speed * emitter.scale;

      // Local to world: the effect's heading turns x and y, z is up in both;
      // a placed effect's whole matrix turns all three
      // (`ParticleSystemInstance.cpp:158-162,194-197`).
      const cos = emitter.cos;
      const sin = emitter.sin;
      const b = emitter.basis;
      const wx = b === null
        ? emitter.originX + lx * cos - ly * sin
        : emitter.originX + b[0] * lx + b[3] * ly + b[6] * lz;
      const wy = b === null
        ? emitter.originY + lx * sin + ly * cos
        : emitter.originY + b[1] * lx + b[4] * ly + b[7] * lz;
      const wz = b === null ? emitter.originZ + lz : emitter.originZ + b[2] * lx + b[5] * ly + b[8] * lz;
      const wvx = b === null ? vlx * cos - vly * sin : b[0] * vlx + b[3] * vly + b[6] * vlz;
      const wvy = b === null ? vlx * sin + vly * cos : b[1] * vlx + b[4] * vly + b[7] * vlz;
      const wvz = b === null ? vlz : b[2] * vlx + b[5] * vly + b[8] * vlz;

      this.px[i] = wx;
      this.py[i] = wy;
      this.pz[i] = wz;
      this.vx[i] = wvx;
      this.vy[i] = wvy;
      this.vz[i] = wvz;
      // Back-dated by one step, the original's own fix for a particle that is
      // drawn before it is ever updated and would otherwise have no length to
      // stretch along (`ParticleSystemInstance.cpp:249`).
      this.lastX[i] = wx - wvx * dt;
      this.lastY[i] = wy - wvy * dt;
      this.lastZ[i] = wz - wvz * dt;
      const sx = baseX * emitter.scale;
      const sy = baseY * emitter.scale;
      const sz = baseZ * emitter.scale;
      this.startX[i] = b === null ? emitter.originX + sx * cos - sy * sin : emitter.originX + b[0] * sx + b[3] * sy + b[6] * sz;
      this.startY[i] = b === null ? emitter.originY + sx * sin + sy * cos : emitter.originY + b[1] * sx + b[4] * sy + b[7] * sz;
      this.startZ[i] = b === null ? emitter.originZ + sz : emitter.originZ + b[2] * sx + b[5] * sy + b[8] * sz;
      this.lifeS[i] = life;
      this.leftS[i] = life;
      this.halfW[i] = halfW;
      this.halfH[i] = halfH;
      const [begin, end] = def.rotationStartDeg;
      this.rotDeg[i] = begin + this.random() * (end - begin);
      // `ROTATION_TYPE_CCW` spins the other way; every other type either does
      // not spin or is driven by the rotation curve, which this build folds
      // into the same speed. `ParticleProperty.h:13-19`.
      this.rotSpeed[i] = def.rotationType === 3 ? -def.rotationSpeedDegS : def.rotationSpeedDegS;
      this.frameTimer[i] = 0;
      this.frame[i] =
        def.texAniRandomStart && def.frames.length > 1
          ? Math.min(def.frames.length - 1, Math.floor(this.random() * def.frames.length))
          : 0;
      this.owner[i] = ownerIndex;
      this.outScaleX[i] = sampleCurve(def.scaleX, 0);
      this.outScaleY[i] = sampleCurve(def.scaleY, 0);
      this.outR[i] = sampleCurve(def.red, 0);
      this.outG[i] = sampleCurve(def.green, 0);
      this.outB[i] = sampleCurve(def.blue, 0);
      this.outA[i] = sampleCurve(def.alpha, 0);

      this.count += 1;
      emitter.live += 1;
    }
  }

  /** Reused so `emit` allocates nothing per particle. */
  private readonly offset = new Float32Array(3);

  private shapeOffset(def: EmitterDef, emittingSize: number): Float32Array {
    const out = this.offset;
    out[0] = out[1] = out[2] = 0;
    if (def.shape === 1 || def.shape === 3) {
      let x = this.random() * 2 - 1;
      let y = this.random() * 2 - 1;
      let z = def.shape === 3 ? this.random() * 2 - 1 : 0;
      const length = Math.hypot(x, y, z);
      if (length > 0) {
        x /= length;
        y /= length;
        z /= length;
      }
      const reach = def.emitFromEdge
        ? def.radiusMm + emittingSize
        : this.random() * def.radiusMm + emittingSize;
      out[0] = x * reach;
      out[1] = y * reach;
      out[2] = z * reach;
    } else if (def.shape === 2) {
      out[0] = (this.random() - 0.5) * def.boxMm[0] + emittingSize;
      out[1] = (this.random() - 0.5) * def.boxMm[1] + emittingSize;
      out[2] = (this.random() - 0.5) * def.boxMm[2] + emittingSize;
    }
    return out;
  }
}

/** One axis of a `TimeEventPosition` row set: `[time, x, y, z]`. */
function sampleAxis(rows: Curve, t: number, axis: number): number {
  const n = rows.length;
  if (n === 0) return 0;
  if (n === 1) return rows[0][axis];
  if (t <= rows[0][0]) return rows[0][axis];
  const last = rows[n - 1];
  if (t >= last[0]) return last[axis];
  for (let i = 1; i < n; i++) {
    if (t > rows[i][0]) continue;
    const lo = rows[i - 1];
    const hi = rows[i];
    const span = hi[0] - lo[0];
    if (span <= 0) return hi[axis];
    const alpha = (t - lo[0]) / span;
    return lo[axis] * (1 - alpha) + hi[axis] * alpha;
  }
  return last[axis];
}
